import { createContext, RouterContextProvider } from 'react-router';
import type { Session } from './auth-client';

/**
 * Authentication Context for Router
 *
 * This context makes authentication state available throughout the router
 * via the getContext mechanism. It's populated on every navigation and fetch,
 * ensuring fresh session data is always available to loaders and actions.
 */
export const authContext = createContext<Session | null>();

/**
 * Create Router Context Provider
 *
 * This function is called by React Router for every navigation and fetch
 * to create a fresh context instance. It provides a placeholder for the
 * authentication context that will be populated by route loaders.
 *
 * Note: Since getContext must be synchronous but getSession is async,
 * we initialize with null and let individual loaders fetch the session
 * as needed. This provides a consistent context structure while allowing
 * async session fetching in loaders.
 *
 * @returns {RouterContextProvider} Context provider with auth context
 *
 * @example
 * ```tsx
 * // In router configuration
 * const router = createBrowserRouter(routes, {
 *   getContext: createRouterContext,
 * });
 *
 * // In a loader
 * export async function loader({ context }: Route.LoaderArgs) {
 *   const session = await authClient.getSession();
 *   if (!session) throw redirect('/login');
 *   // ... use session
 * }
 * ```
 */
export function createRouterContext(): RouterContextProvider {
  const context = new RouterContextProvider();

  // Initialize auth context with null
  // Loaders will fetch session data as needed
  context.set(authContext, null);

  return context;
}
